import React from 'react';
import { Text, Linking, StyleSheet, type StyleProp, type TextStyle } from 'react-native';
import { useTheme, TYPOGRAPHY } from '../../theme';
import { linkifyCaption } from '../../utils/newsCaption';

interface LinkifiedTextProps {
  children: string;
  style?: StyleProp<TextStyle>;
  /** Clamps the rendered text (RN-web maps this to -webkit-line-clamp) —
   *  the feed card body uses this to cap a long post at 3 lines. Links stay
   *  tappable; one that falls past the clip just isn't reachable, same as
   *  any other clipped text. */
  numberOfLines?: number;
}

/**
 * Body text with tappable URLs — news captions are mostly announcements
 * with links (linkifyCaption shortens the display text and keeps trailing
 * punctuation out of the link). Used by NewsItem and by PostCard's caption
 * in news mode; observation captions rarely carry URLs and stay plain.
 */
export function LinkifiedText({ children, style, numberOfLines }: LinkifiedTextProps) {
  const { colors } = useTheme();
  return (
    <Text style={style} numberOfLines={numberOfLines}>
      {linkifyCaption(children).map((seg, i) =>
        seg.type === 'link' ? (
          <Text
            key={i}
            accessibilityRole="link"
            accessibilityLabel={`Open link ${seg.text}`}
            style={[styles.link, { color: colors.news }]}
            onPress={() => {
              Linking.openURL(seg.url).catch(() => {});
            }}>
            {seg.text}
          </Text>
        ) : (
          <Text key={i}>{seg.text}</Text>
        )
      )}
    </Text>
  );
}

const styles = StyleSheet.create({
  link: {
    fontFamily: TYPOGRAPHY.label.fontFamily, // medium — links read a step firmer than prose
  },
});
